import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { useExecuteAction, useUpdateAssignment } from '@hooks/useCases';
import { useSchema } from '@hooks/useSchema';
import { useStore } from '@store/index';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { ActionDataParams } from '@/types/actions';
import type { FieldGroup } from '@/types/formSchema';
import type { AssigneeUpdatePlan } from '@/utils/assigneeConfirmation';
import {
  actionRequiresValidation,
  type StatusUpdatePlan,
  validateCasesWithInputGroups,
} from '@/utils/bulkStatusUpdate';
import { getActionEnum, transformActionForHumanCall } from '@/utils/caseActions';

/**
 * Validates status update plans and filters out invalid cases.
 * Returns the processed plans and information about skipped cases.
 * Uses inputGroups from schema service for validation (supports BSR fallback).
 */
function validateAndFilterPlans(
  statusUpdatePlans: StatusUpdatePlan[],
  cases: GetCaseRecordResponse[],
  inputGroups: FieldGroup[],
): {
  processedPlans: StatusUpdatePlan[];
  totalSkipped: number;
  skippedCaseCodes: string[];
} {
  let totalSkipped = 0;
  const skippedCaseCodes: string[] = [];

  const processedPlans = statusUpdatePlans
    .map(plan => {
      // Skip validation for actions that don't require it
      if (!actionRequiresValidation(plan.action)) {
        return plan;
      }

      // Get the cases for this plan
      const planCases = cases.filter(
        (c: GetCaseRecordResponse) => c.id && plan.caseIds.includes(c.id),
      );
      // Use validateCasesWithInputGroups (new API that accepts inputGroups from schema service)
      const { validCases, invalidCases } = validateCasesWithInputGroups(
        planCases,
        plan.action,
        inputGroups,
      );

      // Track skipped cases
      totalSkipped += invalidCases.length;
      invalidCases.forEach(ic => {
        skippedCaseCodes.push(ic.caseCode || ic.caseId);
      });

      // Return updated plan with only valid case IDs
      return {
        ...plan,
        caseIds: validCases.map(c => c.id!),
        count: validCases.length,
      };
    })
    .filter(plan => plan.count > 0); // Remove plans with no valid cases

  return { processedPlans, totalSkipped, skippedCaseCodes };
}

/**
 * Shows a toast notification for skipped cases due to validation errors.
 */
function showSkippedCasesToast(totalSkipped: number, skippedCaseCodes: string[]): void {
  if (totalSkipped > 0) {
    const caseLabel = totalSkipped === 1 ? 'case' : 'cases';
    const caseList = skippedCaseCodes.slice(0, 3).join(', ');
    const suffix = skippedCaseCodes.length > 3 ? '...' : '';
    toast.error(
      `${totalSkipped} ${caseLabel} skipped due to validation errors: ${caseList}${suffix}`,
      { duration: 5000 },
    );
  }
}

export interface UseBulkSelectionProps {
  cases?: any[];
  onSelectionChange?: (selectedIds: Set<string>) => void;
  /** External selected IDs state (from store) */
  selectedIds?: Set<string>;
  /** External setter for selected IDs (from store) */
  setSelectedIds?: (ids: Set<string>) => void;
}

interface BulkStatusUpdateModal {
  isOpen: boolean;
  selectedCases: any[];
}

interface BulkAssigneeUpdateModal {
  isOpen: boolean;
  assigneeUpdatePlan: AssigneeUpdatePlan | null;
  selectedCases: any[];
}

interface StatusConfirmationDialogState {
  isOpen: boolean;
  statusUpdatePlans: StatusUpdatePlan[];
  isIndividual: boolean;
  actionDataParams?: ActionDataParams;
}

export interface UseBulkSelectionReturn {
  selectedIds: Set<string>;
  handleSelectAllCurrentPage: (selected: boolean) => void;
  handleClearAllSelections: () => void;
  handleBulkStatusUpdate: () => void;
  handleBulkAssigneeSelect: (
    assigneeUserId: string,
    caseIds?: Set<string>,
    teamId?: string,
  ) => Promise<void>;
  setSelectedIds: (ids: Set<string>) => void;
  selectedCases: any[];
  canBulkAssign: boolean;
  bulkStatusUpdateModal: BulkStatusUpdateModal;
  handleConfirmBulkStatusUpdate: (
    statusUpdatePlans: StatusUpdatePlan[],
    note?: string,
  ) => Promise<void>;
  handleCancelBulkStatusUpdate: () => void;
  bulkAssigneeUpdateModal: BulkAssigneeUpdateModal;
  handleConfirmBulkAssigneeUpdate: (note?: string) => Promise<void>;
  handleCancelBulkAssigneeUpdate: () => void;
  // Status confirmation dialog (for individual and bulk actions)
  statusConfirmationDialog: StatusConfirmationDialogState;
  handleIndividualCaseAction: (
    action: string,
    caseId: string,
    actionDataParams?: ActionDataParams,
  ) => void;
  showStatusConfirmation: (plans: StatusUpdatePlan[], isIndividual?: boolean) => void;
  handleStatusConfirmationClose: () => void;
  handleStatusConfirmationConfirm: (note?: string, scheduledAt?: string) => void;
}

export const useBulkSelection = (props?: UseBulkSelectionProps): UseBulkSelectionReturn => {
  // Internal state (used when external state is not provided)
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());

  // Use external state if provided, otherwise use internal state
  const selectedIds = props?.selectedIds ?? internalSelectedIds;
  const setSelectedIds = props?.setSelectedIds ?? setInternalSelectedIds;

  const [bulkStatusUpdateModal, setBulkStatusUpdateModal] = useState<BulkStatusUpdateModal>({
    isOpen: false,
    selectedCases: [],
  });
  const [bulkAssigneeUpdateModal, setBulkAssigneeUpdateModal] = useState<BulkAssigneeUpdateModal>({
    isOpen: false,
    assigneeUpdatePlan: null,
    selectedCases: [],
  });
  const [statusConfirmationDialog, setStatusConfirmationDialog] =
    useState<StatusConfirmationDialogState>({
      isOpen: false,
      statusUpdatePlans: [],
      isIndividual: false,
    });
  const executeAction = useExecuteAction();
  const updateAssignment = useUpdateAssignment();
  const { getSelectedWorkflow } = useStore();

  // Destructure props to avoid dependency issues
  const { cases: propsCases, onSelectionChange } = props || {};
  const cases = useMemo(() => propsCases || [], [propsCases]);
  const selectedWorkflow = getSelectedWorkflow();

  // Fetch schema from schema service (supports BSR fallback)
  const { inputGroups } = useSchema(selectedWorkflow?.id);

  const handleSelectAllCurrentPage = useCallback(
    (selected: boolean) => {
      const newSelection = new Set(selectedIds);
      cases.forEach(item => {
        if (item.id) {
          if (selected) {
            newSelection.add(item.id);
          } else {
            newSelection.delete(item.id);
          }
        }
      });
      setSelectedIds(newSelection);
      onSelectionChange?.(newSelection);
    },
    [cases, selectedIds, setSelectedIds, onSelectionChange],
  );

  const handleClearAllSelections = useCallback(() => {
    const newSelection = new Set<string>();
    setSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
  }, [setSelectedIds, onSelectionChange]);

  const setSelectedIdsWithCallback = useCallback(
    (ids: Set<string>) => {
      setSelectedIds(ids);
      onSelectionChange?.(ids);
    },
    [setSelectedIds, onSelectionChange],
  );

  // Get selected cases
  const selectedCases = useMemo(
    () => cases.filter(case_ => case_.id && selectedIds.has(case_.id)),
    [cases, selectedIds],
  );

  // Check if selected cases can be bulk assigned (allow if ANY case can be assigned)
  const canBulkAssign = useMemo(
    () =>
      selectedCases.length > 0 &&
      selectedCases.some(case_ => case_.status_flags?.blocked_on_manual_task),
    [selectedCases],
  );

  // Helper function to show result toasts
  const showResultToasts = useCallback((successful: number, failed: number) => {
    const totalCases = successful + failed;
    if (failed === 0) {
      toast.success(
        `Action executed successfully${successful > 1 ? ` for all ${successful} cases` : ''}`,
      );
    } else if (successful === 0) {
      toast.error(`Action failed${totalCases > 1 ? ` for all ${totalCases} cases` : ''}`);
    } else {
      toast.success(`Action completed: ${successful} successful, ${failed} failed`, {
        duration: 5000,
      });
    }
  }, []);

  // Generic batch execution helper
  const executeBatchOperation = useCallback(
    async (
      operation: (caseId: string) => Promise<any>,
      operationName: string,
      targetIds?: Set<string>,
    ): Promise<{ successful: number; failed: number; failedCases: string[] }> => {
      let successful = 0;
      let failed = 0;
      const failedCases: string[] = [];

      const caseIds = Array.from(targetIds || selectedIds);
      const BATCH_SIZE = 10;

      // Process cases in batches of 10
      for (let i = 0; i < caseIds.length; i += BATCH_SIZE) {
        const batch = caseIds.slice(i, i + BATCH_SIZE);

        // Execute batch in parallel
        const batchPromises = batch.map(async caseId => {
          try {
            await operation(caseId);
            return { success: true, caseId };
          } catch (error: any) {
            console.error(`Failed to ${operationName} for case ${caseId}:`, error);
            return { success: false, caseId, error };
          }
        });

        // Wait for current batch to complete
        const batchResults = await Promise.all(batchPromises);

        // Process batch results
        batchResults.forEach(result => {
          if (result.success) {
            successful++;
          } else {
            failed++;
            failedCases.push(result.caseId);
          }
        });
      }

      return { successful, failed, failedCases };
    },
    [selectedIds],
  );

  // Handle bulk status update - show modal
  const handleBulkStatusUpdate = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.error('No cases selected');
      return;
    }

    setBulkStatusUpdateModal({
      isOpen: true,
      selectedCases,
    });
  }, [selectedIds.size, selectedCases]);

  // Handle confirmation of bulk status update
  const handleConfirmBulkStatusUpdate = useCallback(
    async (statusUpdatePlans: StatusUpdatePlan[], note?: string) => {
      if (statusUpdatePlans.length === 0) {
        return;
      }

      // Close the modal
      setBulkStatusUpdateModal({
        isOpen: false,
        selectedCases: [],
      });

      // Validate cases for actions requiring validation and filter out invalid ones
      // Uses inputGroups from schema service (supports BSR fallback)
      const { processedPlans, totalSkipped, skippedCaseCodes } = validateAndFilterPlans(
        statusUpdatePlans,
        cases,
        inputGroups,
      );

      // Show toast if cases were skipped due to validation errors
      showSkippedCasesToast(totalSkipped, skippedCaseCodes);

      // Check if any cases remain to process
      if (processedPlans.length === 0) {
        return;
      }

      const totalCases = processedPlans.reduce((total, plan) => total + plan.count, 0);

      // Show progress toast
      const progressToast = toast.loading(
        totalCases === 1 ? `Executing action...` : `Executing actions for ${totalCases} cases...`,
      );

      try {
        let totalSuccessful = 0;
        let totalFailed = 0;

        // Execute each plan
        for (const plan of processedPlans) {
          const actionEnum = getActionEnum(
            plan.action,
            selectedWorkflow?.workflow_type,
            plan.caseState,
          );
          if (!actionEnum) {
            console.error(`Invalid action: ${plan.action}`);
            totalFailed += plan.count;
            continue;
          }

          const { successful, failed } = await executeBatchOperation(
            caseId =>
              executeAction.mutateAsync({
                caseId,
                action: actionEnum,
                skipToast: true, // Skip individual toasts during bulk operation
                actionDataParams: note ? { actionNote: note } : undefined,
              }),
            'execute action',
            new Set(plan.caseIds),
          );

          totalSuccessful += successful;
          totalFailed += failed;
        }

        // Show final result toast
        showResultToasts(totalSuccessful, totalFailed);
      } catch (error) {
        console.error('Bulk status update execution failed:', error);
        toast.error('Bulk status update execution failed');
      } finally {
        // Always dismiss the progress toast
        toast.dismiss(progressToast);
      }

      // Clear selections after action completes
      const newSelection = new Set<string>();
      setSelectedIds(newSelection);
      onSelectionChange?.(newSelection);
    },
    [
      cases,
      inputGroups,
      selectedWorkflow?.workflow_type,
      executeBatchOperation,
      executeAction,
      showResultToasts,
      setSelectedIds,
      onSelectionChange,
    ],
  );

  // Handle cancellation of bulk status update
  const handleCancelBulkStatusUpdate = useCallback(() => {
    setBulkStatusUpdateModal({
      isOpen: false,
      selectedCases: [],
    });
  }, []);

  // Helper function to execute assignments for all selected cases (batch processing)
  const executeAssignmentsForSelectedCases = useCallback(
    async (
      assigneeUserId: string,
      targetIds?: Set<string>,
      noteContent?: string,
      teamId?: string,
    ) => {
      return executeBatchOperation(
        caseId =>
          updateAssignment.mutateAsync({
            caseId,
            assigned: assigneeUserId,
            noteContent,
            teamId,
          }),
        'assign case',
        targetIds,
      );
    },
    [executeBatchOperation, updateAssignment],
  );

  // Handle bulk assignee selection - show confirmation dialog
  const handleBulkAssigneeSelect = useCallback(
    async (assigneeUserId: string, caseIds?: Set<string>, teamId?: string) => {
      const targetIds = caseIds || selectedIds;

      if (targetIds.size === 0) {
        toast.error('No cases selected');
        return;
      }

      // For individual case assignments, skip the canBulkAssign check since it's based on selectedCases
      if (!caseIds && !canBulkAssign) {
        toast.error('Selected cases cannot be assigned');
        return;
      }

      // Create assignee update plan
      const assigneeUpdatePlan: AssigneeUpdatePlan = {
        assigneeUserId,
        caseIds: Array.from(targetIds),
        count: targetIds.size,
        teamId,
      };

      // Get selected cases for the confirmation dialog
      const selectedCasesForModal = cases.filter(case_ => case_.id && targetIds.has(case_.id));

      // Show confirmation dialog
      setBulkAssigneeUpdateModal({
        isOpen: true,
        assigneeUpdatePlan,
        selectedCases: selectedCasesForModal,
      });
    },
    [selectedIds, canBulkAssign, cases],
  );

  // Handle confirmation of bulk assignee update
  const handleConfirmBulkAssigneeUpdate = useCallback(
    async (note?: string) => {
      const { assigneeUpdatePlan } = bulkAssigneeUpdateModal;

      if (!assigneeUpdatePlan) {
        return;
      }

      // Close the modal first
      setBulkAssigneeUpdateModal({
        isOpen: false,
        assigneeUpdatePlan: null,
        selectedCases: [],
      });

      // Show progress toast with appropriate message for single vs multiple cases
      const isSingleCase = assigneeUpdatePlan.count === 1;
      const progressToast = toast.loading(
        isSingleCase ? `Assigning case...` : `Assigning ${assigneeUpdatePlan.count} cases...`,
      );

      try {
        // Filter to only include assignable cases (and not already assigned to target)
        const assignableCaseIds = assigneeUpdatePlan.caseIds.filter(caseId => {
          const case_ = cases.find(c => c.id === caseId);
          return (
            case_?.status_flags?.blocked_on_manual_task &&
            case_.assigned !== assigneeUpdatePlan.assigneeUserId
          );
        });

        const targetIds = new Set(assignableCaseIds);
        const { successful, failed } = await executeAssignmentsForSelectedCases(
          assigneeUpdatePlan.assigneeUserId,
          targetIds,
          note,
          assigneeUpdatePlan.teamId,
        );

        // Show result toast only for bulk operations — single-case assignments
        // already show "Task reassigned successfully" via useUpdateAssignment.
        if (assigneeUpdatePlan.count > 1) {
          showResultToasts(successful, failed);
        }

        // Clear selections after assignment completes (only for bulk operations)
        if (assigneeUpdatePlan.count > 1) {
          const newSelection = new Set<string>();
          setSelectedIds(newSelection);
          onSelectionChange?.(newSelection);
        }
      } catch (error) {
        console.error('Bulk assignment failed:', error);
        toast.error('Bulk assignment failed');
      } finally {
        // Always dismiss the progress toast
        toast.dismiss(progressToast);
      }
    },
    [
      bulkAssigneeUpdateModal,
      cases,
      executeAssignmentsForSelectedCases,
      showResultToasts,
      setSelectedIds,
      onSelectionChange,
    ],
  );

  // Handle cancellation of bulk assignee update
  const handleCancelBulkAssigneeUpdate = useCallback(() => {
    setBulkAssigneeUpdateModal({
      isOpen: false,
      assigneeUpdatePlan: null,
      selectedCases: [],
    });
  }, []);

  // ============ Status Confirmation Dialog Handlers ============

  // Handle individual case action - transform and show confirmation dialog
  const handleIndividualCaseAction = useCallback(
    (action: string, caseId: string, actionDataParams?: ActionDataParams) => {
      // Transform action for human call states
      const case_ = cases.find((c: GetCaseRecordResponse) => c.id === caseId);
      const transformedAction = transformActionForHumanCall(action, case_?.state);

      // Create a single status update plan for this case
      const statusUpdatePlans: StatusUpdatePlan[] = [
        {
          action: transformedAction,
          caseIds: [caseId],
          count: 1,
          caseState: case_?.state,
        },
      ];

      // Show confirmation dialog, storing any actionDataParams from StatusDropdown
      setStatusConfirmationDialog({
        isOpen: true,
        statusUpdatePlans,
        isIndividual: true,
        actionDataParams,
      });
    },
    [cases],
  );

  // Show status confirmation dialog with given plans (for bulk updates from modal)
  const showStatusConfirmation = useCallback((plans: StatusUpdatePlan[], isIndividual = false) => {
    setStatusConfirmationDialog({
      isOpen: true,
      statusUpdatePlans: plans,
      isIndividual,
    });
  }, []);

  // Close status confirmation dialog
  const handleStatusConfirmationClose = useCallback(() => {
    setStatusConfirmationDialog({
      isOpen: false,
      statusUpdatePlans: [],
      isIndividual: false,
      actionDataParams: undefined,
    });
  }, []);

  // Confirm status update and execute
  const handleStatusConfirmationConfirm = useCallback(
    (note?: string, scheduledAt?: string) => {
      const { statusUpdatePlans, isIndividual, actionDataParams } = statusConfirmationDialog;

      // For individual actions with actionDataParams from StatusDropdown, execute directly
      if (isIndividual && statusUpdatePlans.length === 1) {
        const plan = statusUpdatePlans[0];
        const actionEnum = getActionEnum(
          plan.action,
          selectedWorkflow?.workflow_type,
          plan.caseState,
        );

        if (actionEnum && plan.caseIds.length === 1) {
          // Validate individual case for actions that require validation
          const { processedPlans, totalSkipped, skippedCaseCodes } = validateAndFilterPlans(
            statusUpdatePlans,
            cases,
            inputGroups,
          );
          if (processedPlans.length === 0) {
            showSkippedCasesToast(totalSkipped, skippedCaseCodes);
            setStatusConfirmationDialog({
              isOpen: false,
              statusUpdatePlans: [],
              isIndividual: false,
              actionDataParams: undefined,
            });
            return;
          }

          // Merge stored actionDataParams with any note/scheduledAt from confirmation dialog
          const mergedParams: ActionDataParams = {
            ...actionDataParams,
            ...(note ? { actionNote: note } : {}),
            ...(scheduledAt ? { scheduled_at: scheduledAt } : {}),
          };

          executeAction.mutate({
            caseId: plan.caseIds[0],
            action: actionEnum,
            actionDataParams: Object.keys(mergedParams).length > 0 ? mergedParams : undefined,
          });

          // Close the dialog
          setStatusConfirmationDialog({
            isOpen: false,
            statusUpdatePlans: [],
            isIndividual: false,
            actionDataParams: undefined,
          });
          return;
        }
      }

      // For bulk operations or fallback, use handleConfirmBulkStatusUpdate
      handleConfirmBulkStatusUpdate(statusUpdatePlans, note);

      // Close the dialog
      setStatusConfirmationDialog({
        isOpen: false,
        statusUpdatePlans: [],
        isIndividual: false,
        actionDataParams: undefined,
      });
    },
    [
      statusConfirmationDialog,
      selectedWorkflow,
      executeAction,
      handleConfirmBulkStatusUpdate,
      cases,
      inputGroups,
    ],
  );

  return {
    selectedIds,
    handleSelectAllCurrentPage,
    handleClearAllSelections,
    handleBulkStatusUpdate,
    handleBulkAssigneeSelect,
    setSelectedIds: setSelectedIdsWithCallback,
    selectedCases,
    canBulkAssign,
    bulkStatusUpdateModal,
    handleConfirmBulkStatusUpdate,
    handleCancelBulkStatusUpdate,
    bulkAssigneeUpdateModal,
    handleConfirmBulkAssigneeUpdate,
    handleCancelBulkAssigneeUpdate,
    // Status confirmation dialog
    statusConfirmationDialog,
    handleIndividualCaseAction,
    showStatusConfirmation,
    handleStatusConfirmationClose,
    handleStatusConfirmationConfirm,
  };
};
